import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { Flow, TransportExtension, UsageProfile } from '@thive/fddm-types';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { assertNever } from '@/helpers/utils';

import { UsageProfileOptions } from '../types';

import {
  setConsolidationAmountPreference,
  setFlowLimitPreference,
  setFlowLoadsPreference,
} from '../preferences';
import { isConsolidationFlowType } from '../guards';

/** @description The updaters of a single flow, see useFlowsUpdaters */
export const useFlowUpdaters = (id: string) => {
  // A stable list: a new one every render would recreate all updaters
  const ids = useMemo(() => [id], [id]);

  return useFlowsUpdaters(ids);
};

/** @description Updaters that edit all given flows at once, in one undo step */
export const useFlowsUpdaters = (ids: string[]) => {
  const { t } = useTranslation();
  const { getPreference } = useFloorplanDesignStore.getState();

  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const getFlowTypes = useCallback(
    () =>
      new Set(
        useFloorplanDesignStore
          .getState()
          .getFlowsByIds(ids)
          .map(({ type }) => type),
      ),
    [ids],
  );

  /**
   * Saves the flows `update` returns a new flow for, flows it returns undefined for are left as
   * they are. Returns an error when none of the flows exist.
   */
  const updateFlows = useCallback(
    (update: (flow: Flow) => Flow | undefined) => {
      const flows = useFloorplanDesignStore.getState().getFlowsByIds(ids);
      if (flows.length === 0) {
        return { error: t('errors:flow.not_found') };
      }

      const nextFlows = flows.flatMap((flow) => update(flow) ?? []);
      if (nextFlows.length > 0) fddmSvc.updateFlows(nextFlows);
    },
    [fddmSvc, ids, t],
  );

  const onNameChange = useCallback(
    (value: string) => {
      if (!value) {
        return { error: t('errors:flow.name_required') };
      }

      // Names are unique, so only a single flow can be renamed
      const { flows } = useFloorplanDesignStore.getState().design.flows;
      const unique =
        ids.length === 1 &&
        flows.filter((item) => !ids.includes(item.id)).every((item) => item.name !== value);
      if (!unique) {
        return { error: t('errors:flow.name_duplicated') };
      }

      return updateFlows((flow) => ({
        ...flow,
        name: value,
      }));
    },
    [ids, t, updateFlows],
  );

  const onFlowVehicleLimitChange = useCallback(
    (value: number) => {
      getFlowTypes().forEach((type) => setFlowLimitPreference(type, value));
      return updateFlows((flow) => ({
        ...flow,
        vehicleLimit: value,
      }));
    },
    [getFlowTypes, updateFlows],
  );

  const onFlowLoadsChange = useCallback(
    (value: number) => {
      const hourlyOrderCounts: [number, ...number[]] = [value, ...new Array(24).fill(value)];

      getFlowTypes().forEach((type) => setFlowLoadsPreference(type, hourlyOrderCounts));
      return updateFlows((flow) => ({
        ...flow,
        hourlyOrderCounts,
      }));
    },
    [getFlowTypes, updateFlows],
  );

  const onPreAnnounceDurationChange = useCallback(
    (value: number) => {
      const types = getFlowTypes();
      if (types.has('TRANSPORT')) {
        fddmSvc.setPreference('flow.transportExtension.preAnnounceDuration', value);
      }
      if (types.has('CONSOLIDATION_STORAGE')) {
        fddmSvc.setPreference('flow.consolidationStorageExtension.preAnnounceDuration', value);
      }

      return updateFlows((flow) => {
        switch (flow.type) {
          case 'TRANSPORT': {
            // if value is 0, we want to set transportExtension to undefined
            let transportExtension: TransportExtension | undefined;
            if (value <= 0) {
              transportExtension = undefined;
            } else if (flow.transportExtension) {
              transportExtension = {
                ...flow.transportExtension,
                preAnnounceDuration: value,
              };
            } else {
              transportExtension = {
                preAnnounceDuration: value,
                maxActivePreAnnouncedAvOrders: getPreference(
                  'flow.transportExtension.maxActivePreAnnouncedAvOrders',
                ),
              };
            }

            return {
              ...flow,
              transportExtension,
            };
          }
          case 'CONSOLIDATION_STORAGE':
            if (!flow.consolidationStorageExtension) {
              console.error(
                `Consolidation storage flow with id ${flow.id} does not have a consolidationStorageExtension`,
              );
              return undefined;
            }

            return {
              ...flow,
              consolidationStorageExtension: {
                ...flow.consolidationStorageExtension,
                preAnnounceDuration: value,
              },
            };
          default:
            return undefined;
        }
      });
    },
    [fddmSvc, getFlowTypes, getPreference, updateFlows],
  );

  const onMaxActivePreAnnouncedAvOrdersChange = useCallback(
    (value: number) => {
      const types = getFlowTypes();
      if (types.has('TRANSPORT')) {
        fddmSvc.setPreference('flow.transportExtension.maxActivePreAnnouncedAvOrders', value);
      }
      if (types.has('CONSOLIDATION_STORAGE')) {
        fddmSvc.setPreference(
          'flow.consolidationStorageExtension.maxActivePreAnnouncedAvOrders',
          value,
        );
      }

      return updateFlows((flow) => {
        switch (flow.type) {
          case 'TRANSPORT':
            if (!flow.transportExtension) {
              console.error(`Transport flow with id ${flow.id} does not have a transportExtension`);
              return undefined;
            }

            return {
              ...flow,
              transportExtension: {
                ...flow.transportExtension,
                maxActivePreAnnouncedAvOrders: value,
              },
            };
          case 'CONSOLIDATION_STORAGE':
            if (!flow.consolidationStorageExtension) {
              console.error(
                `Consolidation storage flow with id ${flow.id} does not have a consolidationStorageExtension`,
              );
              return undefined;
            }

            return {
              ...flow,
              consolidationStorageExtension: {
                ...flow.consolidationStorageExtension,
                maxActivePreAnnouncedAvOrders: value,
              },
            };
          default:
            return undefined;
        }
      });
    },
    [fddmSvc, getFlowTypes, updateFlows],
  );

  const onConsolidationGroupsChange = useCallback(
    (newConsolidationGroupAmount: number) => {
      getFlowTypes().forEach((type) => {
        if (isConsolidationFlowType(type)) {
          setConsolidationAmountPreference(type, newConsolidationGroupAmount);
        }
      });

      return updateFlows((flow) => {
        if (!isConsolidationFlowType(flow.type)) return undefined;

        if (flow.consolidationStorageExtension) {
          return {
            ...flow,
            consolidationStorageExtension: {
              ...flow.consolidationStorageExtension,
              numConsolidations: newConsolidationGroupAmount,
            },
          };
        }

        if (!flow.consolidationRetrievalExtension) return undefined;

        return {
          ...flow,
          consolidationRetrievalExtension: {
            ...flow.consolidationRetrievalExtension,
            numConsolidations: newConsolidationGroupAmount,
          },
        };
      });
    },
    [getFlowTypes, updateFlows],
  );

  const onBatchSizeChange = useCallback(
    (newTotalBatchSize: number) => {
      fddmSvc.setPreference('flow.consolidationStorageExtension.batchSize', newTotalBatchSize);
      return updateFlows(({ consolidationStorageExtension, ...flow }) =>
        consolidationStorageExtension
          ? {
              ...flow,
              consolidationStorageExtension: {
                ...consolidationStorageExtension,
                batchSize: newTotalBatchSize,
              },
            }
          : undefined,
      );
    },
    [fddmSvc, updateFlows],
  );

  const onShipmentSizeChange = useCallback(
    (newShipmentSize: number) => {
      fddmSvc.setPreference('flow.consolidationRetrievalExtension.shipmentSize', newShipmentSize);
      return updateFlows(({ consolidationRetrievalExtension, ...flow }) =>
        consolidationRetrievalExtension
          ? {
              ...flow,
              consolidationRetrievalExtension: {
                ...consolidationRetrievalExtension,
                shipmentSize: newShipmentSize,
              },
            }
          : undefined,
      );
    },
    [fddmSvc, updateFlows],
  );

  const onRetrievalStartDelayChange = useCallback(
    (newRetrievalStartDelay: number) => {
      fddmSvc.setPreference(
        'flow.consolidationRetrievalExtension.retrievalStartDelayInHours',
        newRetrievalStartDelay,
      );
      return updateFlows(({ consolidationRetrievalExtension, ...flow }) =>
        consolidationRetrievalExtension
          ? {
              ...flow,
              consolidationRetrievalExtension: {
                ...consolidationRetrievalExtension,
                retrievalStartDelayInHours: newRetrievalStartDelay,
              },
            }
          : undefined,
      );
    },
    [fddmSvc, updateFlows],
  );

  const onGoodsToPersonsProfileValueChange = useCallback(
    (option: UsageProfileOptions, key: 'startWeight' | 'endWeight' | 'rate', value: number) => {
      const updateUsageProfile = (current: UsageProfile): UsageProfile => {
        const { type, usageProfileExponential, usageProfileLinear } = current;

        if (type === 'LINEAR') {
          if (key === 'rate') {
            return current;
          }

          if (!usageProfileLinear) return current;
          fddmSvc.setPreference(
            `flow.goodsToPersonsExtension.${option}.usageProfileLinear.${key}`,
            value,
          );
          return {
            ...current,
            usageProfileLinear: {
              ...usageProfileLinear,
              [key]: value,
            },
          };
        }

        if (type === 'EXPONENTIAL') {
          if (key !== 'rate') {
            return current;
          }

          fddmSvc.setPreference(
            `flow.goodsToPersonsExtension.${option}.usageProfileExponential.${key}`,
            value,
          );
          return {
            ...current,
            usageProfileExponential: {
              ...usageProfileExponential,
              rate: value,
            },
          };
        }

        return current;
      };

      return updateFlows(({ goodsToPersonsExtension, ...flow }) => {
        const currentProfile = goodsToPersonsExtension?.[option];
        if (!goodsToPersonsExtension || !currentProfile) return undefined;

        return {
          ...flow,
          goodsToPersonsExtension: {
            ...goodsToPersonsExtension,
            [option]: updateUsageProfile(currentProfile),
          },
        };
      });
    },
    [fddmSvc, updateFlows],
  );

  /** @description updates usage profile through the dropdown...  */
  const updateUsageProfile = useCallback(
    (
      current: UsageProfile,
      newType: UsageProfile['type'],
      option: UsageProfileOptions,
    ): UsageProfile => {
      switch (newType) {
        case 'LINEAR':
          return current.type === 'LINEAR'
            ? current
            : {
                type: 'LINEAR',
                usageProfileLinear: {
                  startWeight: getPreference(
                    `flow.goodsToPersonsExtension.${option}.usageProfileLinear.startWeight`,
                  ),
                  endWeight: getPreference(
                    `flow.goodsToPersonsExtension.${option}.usageProfileLinear.endWeight`,
                  ),
                },
              };

        case 'EXPONENTIAL':
          return current.type === 'EXPONENTIAL'
            ? current
            : {
                type: 'EXPONENTIAL',
                usageProfileExponential: {
                  rate: getPreference(
                    `flow.goodsToPersonsExtension.${option}.usageProfileExponential.rate`,
                  ),
                },
              };

        default:
          assertNever(newType);
      }
    },
    [getPreference],
  );

  const onGoodsToPersonsProfileTypeChange = useCallback(
    (option: UsageProfileOptions, value: UsageProfile['type']) =>
      updateFlows(({ goodsToPersonsExtension, ...flow }) => {
        const currentProfile = goodsToPersonsExtension?.[option];
        if (!goodsToPersonsExtension || !currentProfile) return undefined;

        return {
          ...flow,
          goodsToPersonsExtension: {
            ...goodsToPersonsExtension,
            [option]: updateUsageProfile(currentProfile, value, option),
          },
        };
      }),
    [updateFlows, updateUsageProfile],
  );

  const onHandoverCooldownChange = useCallback(
    (newHandoverCooldown: number) =>
      updateFlows(({ goodsToPersonsExtension, ...flow }) =>
        goodsToPersonsExtension
          ? {
              ...flow,
              goodsToPersonsExtension: {
                ...goodsToPersonsExtension,
                handoverCooldown: newHandoverCooldown,
              },
            }
          : undefined,
      ),
    [updateFlows],
  );

  const onConcurrentAisleCountChange = useCallback(
    (newConcurrentAisleCount: number) =>
      updateFlows(({ goodsToPersonsExtension, ...flow }) =>
        goodsToPersonsExtension
          ? {
              ...flow,
              goodsToPersonsExtension: {
                ...goodsToPersonsExtension,
                concurrentAisleCount: newConcurrentAisleCount,
              },
            }
          : undefined,
      ),
    [updateFlows],
  );

  const onProcessDurationChange = useCallback(
    (newProcessDuration: number) =>
      updateFlows((flow) => {
        if (flow.goodsToPersonsExtension) {
          return {
            ...flow,
            goodsToPersonsExtension: {
              ...flow.goodsToPersonsExtension,
              processDuration: newProcessDuration,
            },
          };
        }

        if (!flow.externalFlowExtension) return undefined;

        return {
          ...flow,
          externalFlowExtension: {
            ...flow.externalFlowExtension,
            processDuration: newProcessDuration,
          },
        };
      }),
    [updateFlows],
  );

  const onSequencingHorizonWindowChange = useCallback(
    (newSequencingHorizonWindow: number) =>
      updateFlows(({ goodsToPersonsExtension, ...flow }) =>
        goodsToPersonsExtension
          ? {
              ...flow,
              goodsToPersonsExtension: {
                ...goodsToPersonsExtension,
                sequencingHorizonWindow: newSequencingHorizonWindow,
              },
            }
          : undefined,
      ),
    [updateFlows],
  );

  return {
    onNameChange,
    onFlowVehicleLimitChange,
    onFlowLoadsChange,
    onPreAnnounceDurationChange,
    onMaxActivePreAnnouncedAvOrdersChange,
    onConsolidationGroupsChange,
    onBatchSizeChange,
    onShipmentSizeChange,
    onRetrievalStartDelayChange,
    onGoodsToPersonsProfileValueChange,
    updateUsageProfile,
    onGoodsToPersonsProfileTypeChange,
    onConcurrentAisleCountChange,
    onHandoverCooldownChange,
    onProcessDurationChange,
    onSequencingHorizonWindowChange,
  };
};
